import { useState } from "react";
import axios from "axios";
import { UserCircle, Envelope, LockKey, CheckCircle, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProfileTab() {
  const { user, refresh: refreshAuth } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState(null); // {kind: 'ok'|'err', msg: string}
  const [saving, setSaving] = useState(false);

  const emailChanged = email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
  const nameChanged = name.trim() !== (user?.name || "");
  const wantsPasswordChange = Boolean(newPassword);
  const hasAnyChange = emailChanged || nameChanged || wantsPasswordChange;

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (wantsPasswordChange && newPassword !== confirmPassword) {
      setStatus({ kind: "err", msg: "New passwords do not match" });
      return;
    }
    if (wantsPasswordChange && newPassword.length < 8) {
      setStatus({ kind: "err", msg: "Password must be at least 8 characters" });
      return;
    }
    if (!currentPassword) {
      setStatus({ kind: "err", msg: "Enter your current password to confirm" });
      return;
    }
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (nameChanged) payload.name = name.trim();
      if (emailChanged) payload.email = email.trim().toLowerCase();
      if (wantsPasswordChange) payload.new_password = newPassword;
      const r = await axios.patch(`${API}/auth/profile`, payload, { withCredentials: true });
      setStatus({
        kind: "ok",
        msg: `${r.data?.message || "Profile updated"}${r.data?.changed?.length ? ` (${r.data.changed.join(", ")})` : ""}`,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      if (refreshAuth) await refreshAuth();
    } catch (err) {
      setStatus({ kind: "err", msg: err?.response?.data?.detail || "Update failed — try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-profile-tab">
      <div>
        <h2 className="font-heading text-xl font-semibold mb-1 flex items-center gap-2">
          <UserCircle size={22} weight="bold" className="text-emerald-400" />
          My Profile &amp; Security
        </h2>
        <p className="text-xs text-stone-500 font-body">
          Update your name, sign-in email, and password. Every change requires your <strong>current password</strong> to confirm.
        </p>
      </div>

      <form onSubmit={submit} className="max-w-xl space-y-5">
        {/* Identity */}
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-heading font-medium">Identity</h3>
          <label className="block">
            <span className="text-[11px] text-stone-500 uppercase tracking-wider">Display name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 mt-1"
              data-testid="profile-name-input"
              placeholder="Your name"
            />
          </label>
          <label className="block">
            <span className="text-[11px] text-stone-500 uppercase tracking-wider flex items-center gap-1">
              <Envelope size={11} /> Sign-in email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm font-mono text-white focus:outline-none focus:border-emerald-500 mt-1"
              data-testid="profile-email-input"
              autoComplete="email"
            />
            <p className="text-[10px] text-stone-500 mt-1">Used for login and password-reset. Changing it will require you to log in again next time.</p>
          </label>
        </div>

        {/* Password */}
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-heading font-medium flex items-center gap-2">
            <LockKey size={14} /> Change password <span className="text-[10px] font-body text-stone-500 font-normal">(optional)</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] text-stone-500 uppercase tracking-wider">New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 mt-1"
                data-testid="profile-newpassword-input"
                placeholder="Leave blank to keep current"
                autoComplete="new-password"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-stone-500 uppercase tracking-wider">Confirm new password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 mt-1"
                data-testid="profile-confirmpassword-input"
                autoComplete="new-password"
              />
            </label>
          </div>
          <p className="text-[10px] text-stone-500">Min 8 characters. Must differ from your current password.</p>
        </div>

        {/* Confirm with current password */}
        <div className="bg-amber-600/5 border border-amber-600/30 rounded-xl p-5">
          <label className="block">
            <span className="text-[11px] text-amber-300 uppercase tracking-wider flex items-center gap-1">
              <LockKey size={11} weight="bold" /> Confirm with current password
            </span>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full bg-stone-900 border border-amber-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500 mt-1"
              data-testid="profile-currentpassword-input"
              autoComplete="current-password"
              required
            />
          </label>
        </div>

        {status && (
          <div
            className={`rounded-xl p-3 text-sm flex items-start gap-2 ${
              status.kind === "ok"
                ? "bg-emerald-600/10 border border-emerald-600/30 text-emerald-300"
                : "bg-red-600/10 border border-red-600/30 text-red-300"
            }`}
            data-testid={`profile-status-${status.kind}`}
          >
            {status.kind === "ok" ? <CheckCircle size={16} weight="fill" /> : <Warning size={16} weight="fill" />}
            <span>{status.msg}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <p className="text-[10px] text-stone-500">
            {hasAnyChange ? "You have unsaved changes." : "No pending changes."}
          </p>
          <button
            type="submit"
            disabled={saving || !hasAnyChange || !currentPassword}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm px-5 py-2 rounded-lg"
            data-testid="profile-save-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
